<template>
  <section ref="section" class="-visibility-auto">
    <h2 class="a11y-sr-only" v-text="item.title"></h2>
    <Observer name="fade" :class="{ '-left': item.mediaPosition === 'left'}" @enter="enter">
      <parallax ref="parallax" :from="{ y: -100 }" :to="{ y: 100 }">
        <img v-if="!item.video && imageUrl" class="image" ref="picture" :src="imageLowDefinition + item.image.filter" :alt="item.image.alt || item.title || ' '" slot-scope="{ parallax }" :style="`transform: translate3d(0px, ${ (parallax && parallax.y) ? Math.round(parallax.y) : 0 }px, 0px) scale(1.1)`" sizes="
            (max-width: 768px) 100vw,50vw"
             width="400"
             height="400"
        >
        <div v-else-if="item.video" class="media-wrapper" slot-scope="{ parallax }">
          <img v-if="posterUrl" :class="{'hidden': !pauseVideo}" ref="poster" :src="posterLowDefinition" :alt="item.video.alt || item.title || ' '" :style="`transform: translate3d(0px, ${ (parallax && parallax.y) ? Math.round(parallax.y) : 0 }px, 0px) scale(1.1)`" sizes="
            (max-width: 768px) 100vw,50vw"
            width="400"
            height="400"
          >
          <div class="video-container" :class="{'hidden': pauseVideo && posterUrl}">
            <video ref="video" class="video" playsinline loop preload="auto" :autoplay="item.videoAutoplay" :muted="!audioPlaying">
              <source :src="item.video.url" type="video/mp4">
            </video>
          </div>
          <button type="button" class="btn-autoPlay" :class="{'-paused': pauseVideo}" :aria-label="pauseVideo ? 'Play' : 'Pause'" @click="toggleVideo"></button>
          <button type="button" class="btn-audio" :aria-label="audioPlaying ? 'Mute' : 'Unmute'" @click="toggleAudio">
            <SoundVolumeIcon v-if="audioPlaying" />
            <SoundNoVolumeIcon v-else />
          </button>
        </div>
      </parallax>
    </Observer>
    <div class="row">
      <div class="col-12 col-md-6 text-center" :class="{ 'offset-md-6' : item.mediaPosition === 'left'}">
        <Observer name="fade-up">
          <h2 class="h3 -upper title" v-text="item.title" aria-hidden="true"></h2>
          <div class="text wysiwyg" v-html="item.text"></div>
          <router-link v-if="item.ctaLink && !item.ctaLink.includes('http')" :to="localizedUrl + item.ctaLink" class="btn -stroke font-btn" :disabled="!item.ctaLink" :event="item.ctaLink ? 'click' : ''">
            <span :aria-hidden="item.ctaScreenreader ? 'true' : false">{{ item.ctaText }}</span>
            <span v-if="item.ctaScreenreader" class="a11y-sr-only">{{ item.ctaScreenreader }}</span>
          </router-link>
          <a v-else-if="item.ctaLink && item.ctaLink.includes('http')" :href="item.ctaLink" class="btn -stroke font-btn" :disabled="!item.ctaLink" :event="item.ctaLink ? 'click' : ''">
            <span :aria-hidden="item.ctaScreenreader ? 'true' : false">{{ item.ctaText }}</span>
            <span v-if="item.ctaScreenreader" class="a11y-sr-only">{{ item.ctaScreenreader }}</span>
          </a>
          <div v-else-if="item.ctaText" class="btn -disabled font-btn" disabled aria-hidden="true">{{ $t('Coming soon') }}</div>
        </Observer>
      </div>
    </div>
  </section>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { Parallax } from 'leaps/dist/leaps'
import { ReadyHook } from 'vue-asyncready'
import ImageManager from 'vue-storefront-prismic-ssr/utilities/ImageManager'
import SoundVolumeIcon from './icons/SoundVolumeIcon.vue'
import SoundNoVolumeIcon from './icons/SoundNoVolumeIcon.vue'

export default {
  props: ['item', 'pagePosition', 'siblings-components'],
  name: 'edito_text_image',
  mixins: [ReadyHook],
  data () {
    return {
      appeared: false,
      pauseVideo: !this.item.videoAutoplay,
      audioPlaying: !this.item.videoAutoplay
    }
  },
  computed: {
    localizedUrl () {
      return currentStoreView().url || ''
    },
    imageUrl() {
      return this.item.image ? this.item.image.url : null
    },
    posterUrl() {
      return this.item.video ? this.item.video.poster : null
    },
    imageLowDefinition() {
      return ImageManager.getLowDefinition(this.imageUrl, 400, 1)
    },
    posterLowDefinition() {
      return ImageManager.getLowDefinition(this.posterUrl, 400, 1)
    }
  },
  components: {
    Parallax,
    SoundVolumeIcon,
    SoundNoVolumeIcon
  },
  methods: {
    enter() {
      if (!this.appeared) {
        this.$refs.section.classList.remove('-visibility-auto')
        this.$refs.parallax.updateConfig()
        this.$refs.parallax.update()
        this.appeared = true
        if (this.$refs.picture && this.imageUrl) this.$refs.picture.srcset = ImageManager.getSrcSet(this.imageUrl, 1200, 400, 100, 1)
        if (this.$refs.poster && this.posterUrl) this.$refs.poster.srcset = ImageManager.getSrcSet(this.posterUrl, 1200, 400, 100, 1)
      }
    },
    toggleVideo () {
      if (this.pauseVideo) {
        this.$refs.video.play()
      } else {
        this.$refs.video.pause()
        this.$refs.video.currentTime = 0
      }
      this.pauseVideo = !this.pauseVideo
    },
    toggleAudio () {
      const video = this.$refs.video
      if (video) {
        video.muted = !video.muted
        this.audioPlaying = !video.muted
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/core";

.-icons .title {
  display: inline;
  color: var(--zenith-white) !important;
  background: var(--zenith-theme-primary) !important;
  box-shadow: -2px 0px 0px 6px var(--zenith-theme-primary);
}

section {
  position: relative;
  overflow: hidden;

  .obs-fade-up {
    transition: none;
    opacity:1;
    transform: none;
    .h3, .text, .btn {
      transform: translateY(8rem);
      opacity: 0;
    }
  }

  & > .obs {
    position: absolute;
    width: 50%;
    top: 0;
    height: 100%;
    transform-style: preserve-3d;
    overflow: hidden;
    transition: none;

    &.-left {
      left: 0;
    }

    &:not(.-left) {
      right: 0;
    }

    @include bp(max md) {
      position: relative;
      left: auto;
      right: auto;
      top: auto;
      width: 100%;
      height: auto;
      transform: none;
      margin-bottom: 14rem;
    }
  }

  .h3 {
    @include appear();
    color: var(--zenith-theme-primary);
  }
  .text {
    @include appear(200ms);
    color: var(--zenith-theme-primary);
  }
  .btn {
    @include appear(400ms);
  }
}

.image {
  position: absolute;
  object-fit: cover;
  height: 100%;
  width: 100%;

  @include bp(max md) {
    position: relative;
    width: calc(100% - 3rem);
    margin-left: 1.5rem;
    height: auto;
  }
}

.hidden {
  display: none;
}

.media-wrapper {
  position: absolute;
  object-fit: cover;
  height: 100%;
  width: 100%;
  overflow: hidden;

  @include bp(max md) {
    position: relative;
    aspect-ratio: 1;
  }

  .video-container {
    width: 100%;
    height: 100%;
    overflow: hidden;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  img {
    width: 100%;
    height: 100%;
  }
}

.col-12 {
  display: flex;
  min-height: 50vw;
  padding-top: 10rem;
  padding-bottom: 10rem;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  @include bp(max md) {
    min-height: auto;
    padding-top: 0;
    padding-bottom: 2rem;
  }

  .obs {
    max-width: 60%;
    @include bp(max xl) {
      max-width: 80%;
    }
  }

  h2, h3 {
    display: block;
  }

  .wysiwyg {
    margin-top: 4rem;
    color: var(--zenith-component-text);

    @include bp(max md) {
      margin-top: 2rem;
    }

    /deep/ p {
      color: var(--zenith-theme-primary);
    }
    /deep/ a {
      color: var(--zenith-theme-primary);
      text-decoration: underline;
    }
  }

  .btn {
    margin-top: 4rem;
    @include bp(max md) {
      margin-top: 2rem;
    }
  }
}

</style>
